import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SuperAdminAuthProvider } from './contexts/SuperAdminAuthContext';
import ThemeProvider from './components/ThemeProvider/ThemeProvider';
import { AppRoutesShell } from './components/AppRoutesShell/AppRoutesShell';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import SuperAdminRoute from './components/SuperAdminRoute/SuperAdminRoute';
import Layout from './components/Layout/Layout';
import SuperAdminLayout from './components/SuperAdminLayout/SuperAdminLayout';
import PageLoader from './components/PageLoader/PageLoader';
import DashboardSkeleton from './components/Dashboard/DashboardSkeleton';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { applyAppDocumentMeta } from './marketing/seo/applyDocumentMeta';
import { useAppViewport } from './marketing/utils/useMarketingViewport';
import { isMarketingPath, isPublicIndexablePath } from './marketing/utils/marketingPaths';
import Login from './pages/Login/Login';
import ForgotPassword from './pages/Login/ForgotPassword';
import ResetPassword from './pages/Login/ResetPassword';
import SuperAdminLogin from './pages/SuperAdmin/SuperAdminLogin';
import SuperAdminSetPassword from './pages/SuperAdmin/SuperAdminSetPassword';
import SuperAdminForgotPassword from './pages/SuperAdmin/SuperAdminForgotPassword';
import SuperAdminResetPassword from './pages/SuperAdmin/SuperAdminResetPassword';
import StoreSelection from './pages/StoreSelection/StoreSelection';
import Register from './pages/Register/Register';
import RegisterVerify from './pages/Register/RegisterVerify';
import OAuthCallback from './pages/OAuthCallback/OAuthCallback';
import CreateStore from './pages/CreateStore/CreateStore';
import CreateStoreFromTemplate from './pages/CreateStoreFromTemplate/CreateStoreFromTemplate';
import JoinStore from './pages/JoinStore/JoinStore';
import Privacy from './pages/Privacy/Privacy';
import Terms from './pages/Terms/Terms';
import DataDeletion from './pages/DataDeletion/DataDeletion';
import RootGate from './marketing/RootGate';
import './App.scss';

/**
 * Main App Component
 * Code Splitting: Toutes les pages sont chargées à la demande (lazy loading)
 * Login, StoreSelection, Register, RegisterVerify, CreateStore, CreateStoreFromTemplate, JoinStore are eager (critical paths).
 */

// Lazy load autres pages - chargées uniquement quand nécessaire
const dashboardImport = () => import('./pages/Dashboard/DashboardNew');
const Dashboard = lazy(dashboardImport);
const Orders = lazy(() => import('./pages/Orders/Orders'));
const Centre = lazy(() => import('./pages/Centre/Centre'));
const Clients = lazy(() => import('./pages/Clients/Clients'));
const Admin = lazy(() => import('./pages/Admin/Admin'));
const Notifications = lazy(() => import('./pages/Notifications/Notifications'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const Preferences = lazy(() => import('./pages/Preferences/Preferences'));
const Birthday = lazy(() => import('./pages/Birthday/Birthday'));
const Help = lazy(() => import('./pages/Help/Help'));
const MyCommissions = lazy(() => import('./pages/MyCommissions/MyCommissions'));
const ProductCatalogPage = lazy(() => import('./pages/Tools/Products/ProductCatalogPage'));
const loadSuperAdminPages = () => import('./pages/SuperAdmin/superAdminPages.lazy');
const SuperAdminDashboard = lazy(() =>
  loadSuperAdminPages().then((m) => ({ default: m.SuperAdminDashboard }))
);
const SuperAdminTemplates = lazy(() =>
  import(
    /* webpackChunkName: "super-admin-catalog" */
    './pages/SuperAdmin/SuperAdminTemplates'
  )
);
const SuperAdminPresets = lazy(() =>
  import(
    /* webpackChunkName: "super-admin-presets" */
    './pages/SuperAdmin/SuperAdminPresets'
  )
);
const SuperAdminStores = lazy(() =>
  import(
    /* webpackChunkName: "super-admin-stores" */
    './pages/SuperAdmin/SuperAdminStores'
  )
);
const SuperAdminStoreDetail = lazy(() =>
  import(
    /* webpackChunkName: "super-admin-stores" */
    './pages/SuperAdmin/SuperAdminStoreDetail'
  )
);
const SuperAdminInvitations = lazy(() =>
  loadSuperAdminPages().then((m) => ({ default: m.SuperAdminInvitations }))
);
const SuperAdminTreatments = lazy(() =>
  loadSuperAdminPages().then((m) => ({ default: m.SuperAdminTreatments }))
);
const loadMarketingPages = () => import('./marketing/marketingPages.lazy');
const MarketingGate = lazy(() =>
  loadMarketingPages().then((m) => ({ default: m.MarketingGate }))
);
const MarketingLayout = lazy(() =>
  loadMarketingPages().then((m) => ({ default: m.MarketingLayout }))
);
const ProductOverviewPage = lazy(() =>
  loadMarketingPages().then((m) => ({ default: m.ProductOverviewPage }))
);
const FeaturePage = lazy(() =>
  loadMarketingPages().then((m) => ({ default: m.FeaturePage }))
);
const IntegrationsPage = lazy(() =>
  loadMarketingPages().then((m) => ({ default: m.IntegrationsPage }))
);
const TrainingPage = lazy(() =>
  loadMarketingPages().then((m) => ({ default: m.TrainingPage }))
);
const ContactPage = lazy(() =>
  loadMarketingPages().then((m) => ({ default: m.ContactPage }))
);

const DEFAULT_DOCUMENT_TITLE = 'Zeedcom';
const SUPER_ADMIN_DOCUMENT_TITLE = 'ZC - Admin';

function SuperAdminTabTitle() {
  const location = useLocation();
  useAppViewport();
  useEffect(() => {
    if (isMarketingPath(location.pathname) || isPublicIndexablePath(location.pathname)) return;
    applyAppDocumentMeta(
      location.pathname.startsWith('/super-admin')
        ? SUPER_ADMIN_DOCUMENT_TITLE
        : DEFAULT_DOCUMENT_TITLE
    );
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      dashboardImport();
    }
  }, []);

  return (
    <Router>
            <SuperAdminTabTitle />
            <AuthProvider>
              <SuperAdminAuthProvider>
              <ThemeProvider>
                <AppRoutesShell>
                          <div className="app">
            <Routes>
              {/* Public Routes (no header) */}
              <Route
                path="/login"
                element={
                  <ErrorBoundary>
                    <Login />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/super-admin/login"
                element={
                  <ErrorBoundary>
                    <SuperAdminLogin />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/super-admin/set-password"
                element={
                  <ErrorBoundary>
                    <SuperAdminSetPassword />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/super-admin/forgot-password"
                element={
                  <ErrorBoundary>
                    <SuperAdminForgotPassword />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/super-admin/reset-password"
                element={
                  <ErrorBoundary>
                    <SuperAdminResetPassword />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <ErrorBoundary>
                    <ForgotPassword />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <ErrorBoundary>
                    <ResetPassword />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/register"
                element={
                  <ErrorBoundary>
                    <Register />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/register/verify"
                element={
                  <ErrorBoundary>
                    <RegisterVerify />
                  </ErrorBoundary>
                }
              />

              {/* Public Routes - No Authentication Required */}
              <Route
                path="/oauth/callback"
                element={
                  <ErrorBoundary>
                    <OAuthCallback />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/privacy"
                element={
                  <ErrorBoundary>
                    <Privacy />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/terms"
                element={
                  <ErrorBoundary>
                    <Terms />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/data-deletion"
                element={
                  <ErrorBoundary>
                    <DataDeletion />
                  </ErrorBoundary>
                }
              />

              <Route
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <MarketingGate>
                        <MarketingLayout />
                      </MarketingGate>
                    </Suspense>
                  </ErrorBoundary>
                }
              >
                {['/produit', '/produit/'].map((path) => (
                  <Route key={path} path={path} element={<ProductOverviewPage />} />
                ))}
                {['/produit/:slug', '/produit/:slug/'].map((path) => (
                  <Route key={path} path={path} element={<FeaturePage />} />
                ))}
                {['/integrations', '/integrations/'].map((path) => (
                  <Route key={path} path={path} element={<IntegrationsPage />} />
                ))}
                {['/formation', '/formation/'].map((path) => (
                  <Route key={path} path={path} element={<TrainingPage />} />
                ))}
                {['/contact', '/contact/'].map((path) => (
                  <Route key={path} path={path} element={<ContactPage />} />
                ))}
              </Route>

              {/* Protected Routes - Store Management (no header) */}
              <Route
                path="/stores"
                element={
                  <PrivateRoute>
                    <ErrorBoundary>
                      <StoreSelection />
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />
              <Route
                path="/stores/create"
                element={
                  <PrivateRoute>
                    <ErrorBoundary>
                      <CreateStore />
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />
              <Route
                path="/stores/create-from-template"
                element={
                  <PrivateRoute>
                    <ErrorBoundary>
                      <CreateStoreFromTemplate />
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />
              <Route
                path="/stores/join"
                element={
                  <PrivateRoute>
                    <ErrorBoundary>
                      <JoinStore />
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />

              <Route
                path="/super-admin"
                element={<Navigate to="/super-admin/dashboard" replace />}
              />
              <Route
                path="/super-admin/dashboard"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminDashboard />
                        </Suspense>
                      </ErrorBoundary>
                    </SuperAdminLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/stores"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminStores />
                        </Suspense>
                      </ErrorBoundary>
                    </SuperAdminLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/stores/:storeId"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminStoreDetail />
                        </Suspense>
                      </ErrorBoundary>
                    </SuperAdminLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/templates"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminTemplates />
                        </Suspense>
                      </ErrorBoundary>
                    </SuperAdminLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/presets"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminPresets />
                        </Suspense>
                      </ErrorBoundary>
                    </SuperAdminLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/invitations"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminInvitations />
                        </Suspense>
                      </ErrorBoundary>
                    </SuperAdminLayout>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/treatments"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminTreatments />
                        </Suspense>
                      </ErrorBoundary>
                    </SuperAdminLayout>
                  </SuperAdminRoute>
                }
              />

              {/* Protected Routes with Header */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<DashboardSkeleton />}>
                          <Dashboard />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/orders"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Orders />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/centre"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Centre />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Clients />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Admin />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Notifications />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/outils/produits"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <ProductCatalogPage />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-commissions"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <MyCommissions />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Profile />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/preferences"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Preferences />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/help"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Help />
                        </Suspense>
                      </ErrorBoundary>
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/birthday"
                element={
                  <PrivateRoute>
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <Birthday />
                      </Suspense>
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />

              <Route
                path="/"
                element={
                  <ErrorBoundary>
                    <RootGate />
                  </ErrorBoundary>
                }
              />

              {/* Redirect any unknown routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
              </div>
                </AppRoutesShell>
        </ThemeProvider>
        </SuperAdminAuthProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
